import React, {
  memo, useCallback, useEffect, useMemo, useRef, useState,
} from "react";
import styled, { useTheme } from "styled-components";
import { FiCheck, FiInfo, FiRotateCcw, FiSave, FiTrash2 } from "react-icons/fi";
import { toast } from "react-toastify";
import api from "../../../services/api";
import { SegmentedControl } from "../../activity-logs/components/primitives";
import {
  Section, Skeleton, Widget, WidgetGrid, tokens,
} from "../../activity-logs/styles";
import { IOS } from "../../activity-logs/labels";
import { Notice, Tag } from "../../creditos/styles";
import { Capsule, RoundButton, TintedCapsule } from "../../creditos/components/ControlBar";
import { hairline, surface, tokens as T } from "../../../styles/designTokens";
import { directionColor } from "../dashboard/ReformaDashboard";
import { Control } from "../simulacao/SimulationDashboard";
import { useReformaParams } from "../hooks/useReformaParams";
import useFormacaoPreco from "../hooks/useFormacaoPreco";
import { SEGMENT_DEFS } from "../utils/formacaoPrecoDefs";
import MarginCurve from "./MarginCurve";
import PriceBreakdown from "./PriceBreakdown";
import {
  PERCENT_BASE, TIPO_LABEL, advancedDefaults, buildCurve, buildFormulas, buildLevers, deltaOf, formatDelta,
  money, moneyOfPercent, percentOfMoney, rateRows, resultAtPrice, simpleSegments, toCalcInputs,
} from "./priceData";
import { calcFormacaoPreco } from "../utils/calculoReforma";
import { FluidRange, Num } from "../components/Motion";
import { finePercent } from "../components/Motion";

/**
 * Aba Formação de preço: a calculadora comanda a tela, e tudo abaixo reage a ela.
 * Curva preço × margem (clique para testar um preço), cascata do preço com débito e
 * crédito separados, alavancas que aplicam a solução na calculadora e a conta preenchida
 * com os números digitados. Com segmentos do Cadastro, o cálculo roda no backend e ficam
 * só o caminho do custo para o preço e a cascata, porque o backend pode usar regras que a
 * tela não conhece.
 */

const label2 = ({ theme }) => tokens.label2(theme);
const dot = (color) => <span style={{ width: 7, height: 7, borderRadius: "50%", background: color, opacity: 1 }} />;

const FIELDS = [
  { field: "pctFrete", label: "Frete", hint: "sobre o custo", min: 0, max: 20, step: 0.5 },
  { field: "pctMateriais", label: "Materiais no custo", hint: "geram crédito", min: 0, max: 100, step: 1, serviceOnly: true },
  { field: "pctComissao", label: "Comissão", hint: "sobre o preço", min: 0, max: 20, step: 0.5 },
  { field: "pctOpex", label: "Despesas operacionais", hint: "sobre o preço", min: 0, max: 30, step: 0.5 },
  { field: "pctMargem", label: "Margem desejada", hint: "sobre o preço", min: 0, max: 40, step: 0.5 },
];

const storageKey = (projectId) => `vidal_reforma_calculadora_${projectId}`;
const readSaved = (projectId) => {
  try { return JSON.parse(localStorage.getItem(storageKey(projectId)) || "null"); } catch { return null; }
};
const points = (v) => `${v > 0 ? "+" : "−"}${Math.abs(v).toLocaleString("pt-BR", { maximumFractionDigits: 1 })} p.p.`;
const pct1 = (v) => `${v < 0 ? "−" : ""}${Math.abs(v).toLocaleString("pt-BR", { maximumFractionDigits: 1 })}%`;
const round2 = (v) => Math.round(v * 100) / 100;

const Picker = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
  gap: 14px;
`;

const PickerCard = styled.button`
  padding: 15px 18px 14px;
  border-radius: 16px;
  border: 1px solid ${({ $on, theme }) => hairline(theme, $on)};
  background: ${({ $on, theme }) => surface(theme, $on ? 2 : 0)};
  color: ${({ theme }) => theme.text};
  font: inherit;
  font-variant-numeric: tabular-nums;
  text-align: left;
  cursor: pointer;
  transition: background 160ms ease, border-color 160ms ease;
  &:hover { border-color: ${({ theme }) => hairline(theme, true)}; }
  &:focus-visible { outline: none; box-shadow: 0 0 0 3px ${T.brandRing}; }

  .h { display: flex; align-items: center; gap: 7px; font-size: 0.82em; font-weight: 500; color: ${label2}; }
  .row { display: flex; align-items: baseline; justify-content: space-between; gap: 10px; margin-top: 8px; }
  .v { font-size: 1.28em; font-weight: 600; letter-spacing: -0.035em; white-space: nowrap; }
  .d { font-size: 0.86em; font-weight: 600; white-space: nowrap; }
  .f { margin-top: 3px; font-size: 0.74em; color: ${label2}; }
`;

const FieldBox = styled.label`
  display: block;
  padding: 11px 0;
  border-top: 1px solid ${({ theme }) => hairline(theme)};
  .n { display: block; font-size: 0.82em; font-weight: 500; letter-spacing: -0.01em; }
  .n small { margin-left: 6px; font-weight: 300; color: ${label2}; }
  .box { display: flex; align-items: baseline; gap: 5px; margin-top: 6px; padding: 7px 12px; border-radius: 10px; background: ${({ theme }) => surface(theme, 2)}; }
  .box:focus-within { box-shadow: 0 0 0 3px ${T.brandRing}; }
  .box span { font-size: 0.8em; font-weight: 500; opacity: 0.5; }
  input { flex: 1; width: 100%; min-width: 0; border: none; outline: none; background: transparent; color: ${({ theme }) => theme.text}; font: inherit; font-size: 1.2em; font-weight: 600; letter-spacing: -0.03em; font-variant-numeric: tabular-nums; -moz-appearance: textfield; }
  input::-webkit-outer-spin-button, input::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
  .link { margin-top: 5px; padding: 0; border: none; background: none; color: ${T.brand}; font: inherit; font-size: 0.74em; cursor: pointer; }
`;

// Dois campos que se espelham: percentual e valor em reais
const PairRow = styled.div`
  display: grid;
  grid-template-columns: 76px minmax(0, 1fr);
  gap: 6px;
  margin-top: 7px;

  .cell { display: flex; align-items: center; gap: 3px; height: 30px; padding: 0 8px; border-radius: 8px; background: ${({ theme }) => surface(theme, 2)}; font-size: 0.82em; }
  .cell:focus-within { box-shadow: 0 0 0 2px ${T.brandRing}; }
  .cell span { font-size: 0.86em; font-weight: 500; opacity: 0.5; }
  input { flex: 1; width: 100%; min-width: 0; border: none; outline: none; background: transparent; color: ${({ theme }) => theme.text}; font: inherit; font-weight: 600; letter-spacing: -0.02em; text-align: right; font-variant-numeric: tabular-nums; -moz-appearance: textfield; }
  input::-webkit-outer-spin-button, input::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
  .changed input { color: ${T.brand}; }
`;

const Headline = styled.div`
  margin-top: 16px;
  display: flex;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 14px 34px;

  .hero { flex-shrink: 0; }
  .lab { font-size: 0.76em; color: ${label2}; }
  .big { display: flex; align-items: baseline; margin-top: 4px; font-size: 3em; font-weight: 600; letter-spacing: -0.055em; line-height: 1; white-space: nowrap; font-variant-numeric: tabular-nums; }
  .big .cur { margin-right: 5px; font-size: 0.36em; font-weight: 500; letter-spacing: 0; opacity: 0.5; }
  .big .dec { font-size: 0.42em; font-weight: 500; letter-spacing: -0.02em; opacity: 0.5; }
  .big .unit { margin-left: 2px; font-size: 0.42em; font-weight: 500; letter-spacing: 0; opacity: 0.5; }
  .delta { margin-left: 12px; align-self: center; font-size: 0.36em; font-weight: 600; letter-spacing: -0.02em; }
`;

const StatRail = styled.div`
  flex: 1;
  min-width: 300px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(96px, 1fr));
  gap: 10px 0;

  > div { min-width: 0; padding: 0 14px; border-left: 1px solid ${({ theme }) => hairline(theme)}; }
  .l { font-size: 0.74em; line-height: 1.25; color: ${label2}; }
  .v { margin-top: 4px; font-size: 1.04em; font-weight: 600; letter-spacing: -0.03em; white-space: nowrap; font-variant-numeric: tabular-nums; }
`;

const Sentence = styled.p`
  margin: 12px 0 0;
  font-size: 0.8em;
  font-weight: 300;
  line-height: 1.5;
  color: ${label2};
  strong { font-weight: 500; color: ${({ theme }) => theme.text}; }
`;


// A tabela do preço precisa de mais espaço que as alavancas
const PriceSplit = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 3fr) minmax(0, 2fr);
  gap: 14px;
  @media (max-width: 1200px) { grid-template-columns: minmax(0, 1fr); }
`;

const LeverRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(60px, 92px) auto;
  gap: 12px;
  align-items: center;
  min-height: 54px;
  padding: 6px 0;
  border-bottom: 1px solid ${({ theme }) => hairline(theme)};

  .n { min-width: 0; font-size: 0.86em; font-weight: 500; letter-spacing: -0.012em; }
  .n small { display: block; margin-top: 2px; font-size: 0.86em; font-weight: 300; letter-spacing: 0; color: ${label2}; }
  .e { display: flex; flex-direction: column; gap: 3px; }
  .e span { font-size: 0.72em; text-align: right; color: ${label2}; white-space: nowrap; }
  .track { height: 4px; border-radius: 2px; overflow: hidden; background: ${({ theme }) => surface(theme, 2)}; }
  .track i { display: block; height: 100%; border-radius: 2px; }
  .no { font-size: 0.72em; color: ${({ theme }) => tokens.label3(theme)}; white-space: nowrap; }
`;

const Formula = styled.div`
  padding: 12px 0;
  border-top: 1px solid ${({ theme }) => hairline(theme)};
  .k { display: flex; justify-content: space-between; gap: 12px; font-size: 0.84em; font-weight: 500; letter-spacing: -0.01em; }
  .k b { font-weight: 600; }
  .f { margin-top: 5px; font-size: 0.8em; line-height: 1.5; color: ${label2}; }
`;

const Note = styled.p`
  margin: 8px 0 0;
  font-size: 0.74em;
  font-weight: 300;
  line-height: 1.5;
  color: ${label2};
`;

const RateGrid = styled.div`
  margin-top: 12px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  column-gap: 24px;
  @media (max-width: 640px) { grid-template-columns: 1fr; }

  .r { display: flex; align-items: center; justify-content: space-between; gap: 12px; min-height: 46px; padding: 6px 0; border-top: 1px solid ${({ theme }) => hairline(theme)}; }
  .k { min-width: 0; font-size: 0.82em; font-weight: 500; letter-spacing: -0.01em; line-height: 1.3; }
  .k small { display: block; margin-top: 1px; font-size: 0.86em; font-weight: 300; letter-spacing: 0; color: ${label2}; }
  .v { font-size: 0.92em; font-weight: 600; letter-spacing: -0.02em; white-space: nowrap; }
`;

const ScenarioRow = styled.div`
  display: grid;
  grid-template-columns: minmax(150px, 1fr) minmax(0, 2fr) 30px;
  gap: 16px;
  align-items: center;
  padding: 11px 0;
  border-top: 1px solid ${({ theme }) => hairline(theme)};

  .n { font-size: 0.88em; font-weight: 500; letter-spacing: -0.012em; }
  .n small { display: block; margin-top: 2px; font-size: 0.84em; font-weight: 300; letter-spacing: 0; color: ${label2}; }
  .s { font-size: 0.78em; line-height: 1.6; color: ${label2}; }
  .s b { font-weight: 500; color: ${({ theme }) => theme.text}; }
`;

// Resultado do backend por segmento, com o mesmo debounce de antes e sem resposta antiga
// Segmentos do Cadastro: o servidor pode usar a matriz de despesas, com outra despesa e
// outra parcela com crédito. As duas saem da resposta dele, e com elas a conta local reproduz
// o preço do servidor. Assim a curva, as tabelas e o arraste partem dos mesmos números.
const LOCAL_RULE = { servico: "oficina", normal: "pecas", "ST-monofasico": "veiculos", "base-reduzida": "veiculos" };
const MATCH_TOLERANCE = 0.005;

function calibrate(segment, server, at, source, params) {
  const rule = LOCAL_RULE[source?.tipoRegime] || "pecas";
  const base = { ...segment, rule, rawInputs: segment.inputs, opexFromMatrix: false, matchesServer: true };
  if (!server || !at || !(server.pvFuturo > 0) || !(server.pvAtual > 0)) return { ...base, r: server || {} };
  const paramsAt = { ...params, cbsPlena2033: at.aliquotas.cbsPlena, ibsSpPlena2033: at.aliquotas.ibsPlena, issAtualServicos: at.aliquotas.issAtualServicos };
  const sent = toCalcInputs(at.inputs);
  const opex = (server.opexFuturo || 0) / server.pvFuturo;
  const opexFromMatrix = Math.abs(opex - sent.pctOpex) > 0.0005;
  const T = (Number(paramsAt.cbsPlena2033) + Number(paramsAt.ibsSpPlena2033)) / 100;
  const cost = sent.custo * (1 + sent.pctFrete);
  const withCredit = rule === "oficina" ? sent.pctMateriais : 1;
  const solved = T > 0 && opex > 0
    ? ((cost * (1 - T * withCredit)) / server.pvFuturo - (1 - T - sent.pctComissao - opex - sent.pctMargem)) / (T * opex)
    : 0.8;
  const despesasElegiveis = Number.isFinite(solved) ? Math.max(0, Math.min(1, solved)) : 0.8;

  const check = calcFormacaoPreco(rule, { ...sent, pctOpex: opex, despesasElegiveis }, paramsAt);
  const close = (a, b) => a > 0 && Math.abs(a - b) / b <= MATCH_TOLERANCE;
  const matchesServer = close(check.pvAtual, server.pvAtual) && close(check.pvFuturo, server.pvFuturo);
  const inputs = { ...segment.inputs, despesasElegiveis, ...(opexFromMatrix ? { pctOpex: opex * 100 } : {}) };
  return {
    ...base,
    inputs,
    opexFromMatrix,
    matchesServer,
    // Se a regra local não reproduz o servidor, o preço exibido continua sendo o dele
    r: matchesServer ? calcFormacaoPreco(rule, toCalcInputs(inputs), params) : server,
  };
}

function useAdvancedResults(enabled, segments, aliquotas, projectId) {
  const [results, setResults] = useState({});
  const [calculating, setCalculating] = useState(false);
  const calculatedAt = useRef({});
  const runId = useRef(0);
  const key = JSON.stringify({ s: segments.map((s) => [s.id, s.inputs]), aliquotas });

  useEffect(() => {
    if (!enabled || segments.length === 0) return undefined;
    const id = ++runId.current;
    const timer = setTimeout(async () => {
      setCalculating(true);
      for (const segment of segments) {
        try {
          const resp = await api.post(`/reforma/formacao-preco/calcular/${projectId}`, { segmentoId: segment.id, inputs: segment.inputs, aliquotas });
          if (id !== runId.current) return;
          calculatedAt.current[segment.id] = { inputs: segment.inputs, aliquotas };
          setResults((prev) => ({ ...prev, [segment.id]: resp?.data?.data || null }));
        } catch (e) {
          console.error("[PriceDashboard] Erro ao calcular:", e);
          if (id !== runId.current) return;
          setResults((prev) => ({ ...prev, [segment.id]: null }));
        }
      }
      if (id === runId.current) setCalculating(false);
    }, 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, key, projectId]);

  return { results, calculating, calculatedAt };
}

// Campo numérico que guarda o texto enquanto a pessoa digita, para o recálculo não atropelar
export function DraftNumber({ value, onCommit, ariaLabel, step }) {
  const [draft, setDraft] = useState(null);
  return (
    <input
      type="number"
      step={step}
      aria-label={ariaLabel}
      value={draft !== null ? draft : value}
      onFocus={() => setDraft(String(value))}
      onBlur={() => setDraft(null)}
      onChange={(e) => {
        setDraft(e.target.value);
        if (e.target.value !== "") onCommit(Number(e.target.value));
      }}
    />
  );
}

function PercentControl({ def, locked, value, reference, moneyValue, onPercent, onMoney }) {
  const theme = useTheme();
  const numeric = Number(value) || 0;
  // A régua cresce para o controle sempre refletir o valor digitado
  const max = Math.max(def.max, Math.ceil(numeric * 1.25));
  const position = (v) => Math.max(0, Math.min(100, ((v - def.min) / (max - def.min)) * 100));
  const changed = !locked && numeric !== reference;
  return (
    <Control as="div" style={{ "--p": `${position(numeric)}%`, cursor: "default" }}>
      <span className="top"><span className="name">{def.label} <small style={{ fontWeight: 300, color: tokens.label2(theme) }}>{locked ? "definida na matriz do Cadastro" : def.hint}</small></span></span>
      <fieldset disabled={locked} style={{ border: 0, margin: 0, padding: 0, minWidth: 0, opacity: locked ? 0.55 : 1 }}>
      <PairRow>
        <label className={`cell${changed ? " changed" : ""}`}>
          <DraftNumber value={round2(numeric)} step={def.step} ariaLabel={`${def.label}, em porcentagem`} onCommit={(v) => onPercent(def.field, v)} />
          <span>%</span>
        </label>
        <label className="cell">
          <span>R$</span>
          <DraftNumber value={round2(moneyValue)} step="0.01" ariaLabel={`${def.label}, em reais`} onCommit={(v) => onMoney(def.field, v)} />
        </label>
      </PairRow>
      <span className="slider" style={{ display: "block", "--fill": changed ? T.brand : tokens.label2(theme), "--rest": "rgba(118,118,128,0.28)" }}>
        <span className="mark" style={{ left: `${position(reference)}%` }} />
        <FluidRange min={def.min} max={max} step={def.step} value={numeric} format={finePercent} aria-hidden="true" tabIndex={-1} onChange={(v) => onPercent(def.field, v)} />
      </span>
      </fieldset>
    </Control>
  );
}

function Calculator({
  segment, mode, onMode, allowPriceMode, inputs, defaults, params, baseValue, onChange, onReset, isExample,
  price, onPrice, referencePrice, usingReference,
}) {
  // Sem preço digitado, o preço é resultado da conta e muda junto com o percentual
  let solve = null;
  if (mode === "price") solve = "future";
  if (mode === "margin" && usingReference) solve = "today";
  const isService = segment.rule === "oficina" || segment.isService;
  return (
    <Widget $delay={0} style={{ display: "flex", flexDirection: "column" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
        <div className="w-title">Calculadora</div>
        {isExample
          ? <Tag $color={IOS.gray} style={{ fontSize: "0.76em" }}>Valores de exemplo</Tag>
          : <Capsule type="button" onClick={onReset} title="Voltar aos valores de exemplo deste segmento" style={{ height: 28, padding: "0 10px" }}><FiRotateCcw /> Restaurar</Capsule>}
      </div>
      {allowPriceMode && (
        <div style={{ marginTop: 10 }}>
          <SegmentedControl small value={mode} onChange={onMode} options={[{ key: "price", label: "Achar o preço" }, { key: "margin", label: "Achar a margem" }]} />
        </div>
      )}
      <div style={{ marginTop: 10 }}>
        {mode === "margin" && (
          <FieldBox>
            <span className="n">Preço de venda<small>{usingReference ? "o de hoje" : ""}</small></span>
            <span className="box"><span>R$</span><input type="number" min="0" step="0.01" aria-label="Preço de venda" value={price} onChange={(e) => onPrice(e.target.value)} /></span>
            {!usingReference && <button type="button" className="link" onClick={() => onPrice("")}>Usar o preço de hoje: <Num value={referencePrice} format={money} /></button>}
          </FieldBox>
        )}
        <FieldBox>
          <span className="n">Custo de aquisição ou serviço</span>
          <span className="box"><span>R$</span><input type="number" min="0" step="0.01" aria-label="Custo" value={inputs.custo} onChange={(e) => onChange("custo", e.target.value)} /></span>
        </FieldBox>
        {FIELDS.filter((f) => !f.serviceOnly || isService).map((def) => (
          <PercentControl
            key={def.field}
            def={def}
            locked={def.field === "pctOpex" && segment.opexFromMatrix}
            value={inputs[def.field] ?? 0}
            reference={defaults[def.field] ?? 0}
            moneyValue={moneyOfPercent(def.field, inputs, baseValue)}
            onPercent={onChange}
            onMoney={(field, v) => onChange(field, percentOfMoney({
              field, value: v, segmentId: segment.rule, inputs, params, base: baseValue, solve,
            }))}
          />
        ))}
      </div>
    </Widget>
  );
}

const moneyPieces = (v) => money(v).replace("-", "").replace("R$", "").trim().split(",");
const moneyInteger = (v) => moneyPieces(v)[0];
const moneyDecimal = (v) => (moneyPieces(v)[1] ? `,${moneyPieces(v)[1]}` : "");
const percentNumber = (v) => `${v < 0 ? "−" : ""}${Math.abs(v).toLocaleString("pt-BR", { maximumFractionDigits: 1 })}`;

function BigMoney({ value }) {
  return <><span className="cur">R$</span><Num value={value} format={moneyInteger} /><Num className="dec" value={value} format={moneyDecimal} /></>;
}

function BigPercent({ value }) {
  return <><Num value={value} format={percentNumber} /><span className="unit">%</span></>;
}

function Result({
  segment, mode, atPrice, inputs, testedPrice, curve, calculating, onPickPrice, children,
}) {
  const d = deltaOf(segment.r);
  const target = Number(inputs.pctMargem) || 0;
  const hasMargin = mode === "margin" && atPrice.marginFuture !== null;
  const dropped = hasMargin ? atPrice.marginFuture - atPrice.marginToday : null;
  const profit = atPrice.r?.margemFuturo;

  const stats = mode === "margin"
    ? [
      { key: "today", l: "Margem hoje", v: atPrice.marginToday === null ? "—" : <Num value={atPrice.marginToday} format={pct1} /> },
      { key: "unit", l: profit < 0 ? "Prejuízo por unidade" : "Sobra por unidade", v: profit === undefined ? "—" : <Num value={Math.abs(profit)} format={money} />, color: profit < 0 ? IOS.red : undefined },
      { key: "min", l: `Preço para ${pct1(target)}`, v: <Num value={segment.r?.pvFuturo} format={money} /> },
      curve && { key: "break", l: "Equilíbrio", v: <Num value={curve.breakFuture} format={money} /> },
    ]
    : [
      { key: "today", l: "Preço mínimo hoje", v: <Num value={segment.r?.pvAtual} format={money} /> },
      curve && { key: "keep", l: "Margem se mantiver", v: <Num value={curve.keepTodayPrice} format={pct1} />, color: curve.keepTodayPrice < 0 ? IOS.red : undefined },
      curve && { key: "break", l: "Equilíbrio", v: <Num value={curve.breakFuture} format={money} /> },
    ];

  return (
    <Widget $span={3} $delay={100} style={{ display: "flex", flexDirection: "column" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
        <div style={{ minWidth: 0 }}>
          <div className="w-title">{mode === "margin" ? "Quanto sobra com este preço" : "Quanto cobrar para manter a margem"}</div>
          <div className="w-sub">{segment.titulo} · {segment.subtitulo}{calculating ? " · calculando…" : ""}</div>
        </div>
        {segment.critico && <Tag $color={IOS.red} style={{ fontSize: "0.76em" }}>Segmento crítico</Tag>}
      </div>

      <Headline>
        <div className="hero">
          <div className="lab">{mode === "margin" ? "Margem com CBS/IBS" : "Preço mínimo com CBS/IBS"}</div>
          <div className="big" title={mode === "margin" ? undefined : money(segment.r?.pvFuturo)} style={{ color: hasMargin && atPrice.marginFuture < 0 ? IOS.red : undefined }}>
            {mode === "margin"
              ? (hasMargin ? <BigPercent value={atPrice.marginFuture} /> : "—")
              : <BigMoney value={segment.r?.pvFuturo} />}
            {mode === "margin" && dropped !== null && <span className="delta" style={{ color: directionColor(-dropped) }}><Num value={dropped} format={points} /></span>}
            {mode !== "margin" && d !== null && <span className="delta" style={{ color: directionColor(d) }}><Num value={d} format={formatDelta} /></span>}
          </div>
        </div>
        <StatRail>
          {stats.filter(Boolean).map((s) => (
            <div key={s.key}>
              <div className="l">{s.l}</div>
              <div className="v" style={{ color: s.color }}>{s.v}</div>
            </div>
          ))}
        </StatRail>
      </Headline>

      {!curve && (
        <Sentence>
          {d !== null
            ? `O preço mínimo ${d >= 0 ? "sobe" : "cai"} ${Math.abs(d).toLocaleString("pt-BR", { maximumFractionDigits: 1 })}% no regime futuro para manter a mesma margem líquida desejada.`
            : "Ajuste os valores: comissão, despesas, margem e imposto não podem somar 100% ou mais."}
        </Sentence>
      )}

      {curve && <MarginCurve curve={curve} price={testedPrice} target={target} onPickPrice={onPickPrice} segmentKey={segment.id} />}
      {children}
    </Widget>
  );
}

function Levers({ data, price, onApply }) {
  const theme = useTheme();
  const feasible = data.levers.filter((l) => l.feasible).map((l) => l.effort);
  const max = Math.max(1, ...feasible);
  const easiest = Math.min(...feasible);
  return (
    <Widget $delay={250} style={{ display: "flex", flexDirection: "column" }}>
      <div className="w-title">Como chegar à margem desejada</div>
      <div className="w-sub">
        {data.reached
          ? `Cobrando ${money(price)}, a margem de ${pct1(data.target)} já está garantida, com ${pct1(data.current - data.target)} de folga`
          : `Cobrando ${money(price)}, a margem fica em ${pct1(data.current)}. Basta mexer em uma destas alavancas para chegar a ${pct1(data.target)}`}
      </div>
      {data.reached ? (
        <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 8, padding: "28px 0", fontSize: "0.86em", color: tokens.label(theme) }}>
          <FiCheck style={{ fontSize: 22, color: IOS.green }} />
          Nenhuma alavanca é necessária neste preço.
        </div>
      ) : (
        <div style={{ marginTop: 10 }}>
          {data.levers.map((l) => (
            <LeverRow key={l.key}>
              <div className="n">{l.label}<small>{l.feasible ? `${l.currentText} → ${l.neededText}` : `${l.currentText}. Sozinha, não alcança a meta`}</small></div>
              {l.feasible ? (
                <div className="e">
                  <span>{l.changeText}</span>
                  <div className="track"><i style={{ width: `${(l.effort / max) * 100}%`, background: l.effort === easiest ? IOS.green : tokens.label3(theme) }} /></div>
                </div>
              ) : <div />}
              {l.feasible
                ? <TintedCapsule type="button" onClick={() => onApply(l)} style={{ height: 28, padding: "0 11px" }}>Aplicar</TintedCapsule>
                : <span className="no">—</span>}
            </LeverRow>
          ))}
          <Note>A barra mostra o tamanho da mudança em relação ao valor atual. "Aplicar" leva o valor à calculadora; "Restaurar" desfaz.</Note>
        </div>
      )}
    </Widget>
  );
}

function FormulaCard({ formulas, segment }) {
  return (
    <Widget $span={2} $delay={300}>
      <div className="w-title">A conta, com os seus números</div>
      <div className="w-sub">Markup por dentro, não cumulativo. Muda enquanto você digita</div>
      <div style={{ marginTop: 12 }}>
        {formulas.map((f) => (
          <Formula key={f.key}>
            <div className="k"><span>{f.label}</span><b>{f.result}</b></div>
            <div className="f">{f.text}</div>
          </Formula>
        ))}
      </div>
      {segment.notaAtual && <Note>{segment.notaAtual.replace(/^ATUAL:\s*/, "Atual: ").split(" PV =")[0]}</Note>}
      {segment.notaFuturo && <Note style={{ marginTop: 3 }}>{segment.notaFuturo.replace(/^FUTURO:\s*/, "Futuro: ")}</Note>}
    </Widget>
  );
}

const RatesCard = memo(function RatesCard({ params, advanced, eligible }) {
  return (
    <Widget $span={2} $delay={350}>
      <div className="w-title">Taxas usadas no cálculo</div>
      <div className="w-sub">CBS, IBS e ISS vêm dos Parâmetros. As demais são fixas</div>
      <RateGrid>
        {rateRows(params, advanced, eligible).map((r) => (
          <div className="r" key={r.key}>
            <div className="k">{r.label}<small>{r.note}</small></div>
            <div className="v">{r.value}</div>
          </div>
        ))}
      </RateGrid>
    </Widget>
  );
});

function Scenarios({ projectId, segments, aliquotas }) {
  const [list, setList] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const resp = await api.get(`/reforma/formacao-preco/simulacoes/${projectId}`);
      setList(resp?.data?.data || []);
    } catch (e) {
      console.error("[PriceDashboard] Erro ao listar simulações:", e);
    }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    const calculated = segments.filter((s) => s.r?.pvAtual !== undefined);
    if (calculated.length === 0) {
      toast.info("Aguarde os segmentos calcularem antes de salvar um cenário.");
      return;
    }
    const nome = window.prompt("Nome do cenário (ex: 'Cenário pessimista: margem 5%')", `Cenário ${new Date().toLocaleString("pt-BR")}`);
    if (!nome || !nome.trim()) return;
    setSaving(true);
    try {
      await api.post(`/reforma/formacao-preco/simulacoes/${projectId}`, {
        nome: nome.trim(),
        aliquotas,
        segmentos: calculated.map((s) => ({ segmentoId: s.id, nome: s.titulo, inputs: s.inputs, resultado: s.r })),
      });
      toast.success(`Cenário "${nome.trim()}" salvo.`);
      load();
    } catch (e) {
      console.error(e);
      toast.error("Erro ao salvar cenário.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (sim) => {
    if (!window.confirm(`Remover o cenário "${sim.nome}"?`)) return;
    try {
      await api.delete(`/reforma/formacao-preco/simulacoes/${projectId}/${sim.id}`);
      setList((prev) => prev.filter((s) => s.id !== sim.id));
      toast.success("Cenário removido.");
    } catch (e) {
      console.error(e);
      toast.error("Erro ao remover cenário.");
    }
  };

  return (
    <Widget $span={4} $delay={400}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
        <div style={{ minWidth: 0 }}>
          <div className="w-title">Cenários salvos</div>
          <div className="w-sub">{list.length > 0 ? `${list.length} ${list.length === 1 ? "cenário" : "cenários"}` : "Nenhum cenário salvo ainda. Ajuste a calculadora e salve aqui"}</div>
        </div>
        <TintedCapsule type="button" onClick={save} disabled={saving}><FiSave /> {saving ? "Salvando…" : "Salvar cenário atual"}</TintedCapsule>
      </div>
      <div style={{ marginTop: 12 }}>
        {list.map((sim) => (
          <ScenarioRow key={sim.id}>
            <div className="n">
              {sim.nome}
              <small>{new Date(sim.createdAt).toLocaleString("pt-BR")} · CBS {sim.aliquotas?.cbsPlena}% + IBS {sim.aliquotas?.ibsPlena}%</small>
            </div>
            <div className="s">
              {(sim.segmentos || []).map((s) => (
                <div key={s.segmentoId || s.nome}>
                  <b>{s.nome}</b>: <Num value={s.resultado?.pvAtual} format={money} /> → <Num value={s.resultado?.pvFuturo} format={money} />
                  {s.resultado?.deltaPct !== null && s.resultado?.deltaPct !== undefined && ` (${formatDelta(s.resultado.deltaPct * 100)})`}
                </div>
              ))}
            </div>
            <RoundButton type="button" onClick={() => remove(sim)} title="Remover cenário" aria-label={`Remover o cenário ${sim.nome}`} style={{ width: 30, height: 30 }}><FiTrash2 /></RoundButton>
          </ScenarioRow>
        ))}
      </div>
    </Widget>
  );
}

export default function PriceDashboard() {
  const {
    params, formacaoInputs, initFormacaoSegment, setFormacaoInput,
  } = useReformaParams();
  const fp = useFormacaoPreco();
  const advanced = !fp.loading && fp.hasCadastroAvancado;
  const active = useMemo(() => (fp.segmentos || []).filter((s) => s.ativo !== false), [fp.segmentos]);

  const saved = useRef(readSaved(fp.projectId));
  const [picked, setPicked] = useState(saved.current?.picked || null);
  const [mode, setMode] = useState(saved.current?.mode || "price");
  const [prices, setPrices] = useState(saved.current?.prices || {});
  const restored = useRef(false);

  // Primeiro recupera o que a pessoa digitou; depois semeia só o que falta
  useEffect(() => {
    if (!restored.current) {
      restored.current = true;
      Object.entries(saved.current?.inputs || {}).forEach(([id, fields]) => {
        Object.entries(fields).forEach(([field, value]) => setFormacaoInput(id, field, value));
      });
    }
    SEGMENT_DEFS.forEach((d) => initFormacaoSegment(d.id, d.defaults));
    active.forEach((s) => initFormacaoSegment(s.id, advancedDefaults(s)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  // Fica no navegador, por projeto
  useEffect(() => {
    if (!restored.current) return;
    try {
      localStorage.setItem(storageKey(fp.projectId), JSON.stringify({ picked, mode, prices, inputs: formacaoInputs }));
    } catch { /* preferência só desta sessão */ }
  }, [fp.projectId, picked, mode, prices, formacaoInputs]);

  const aliquotas = useMemo(
    () => ({ cbsPlena: params.cbsPlena2033, ibsPlena: params.ibsSpPlena2033, issAtualServicos: params.issAtualServicos }),
    [params.cbsPlena2033, params.ibsSpPlena2033, params.issAtualServicos],
  );

  const advancedBase = useMemo(() => active.map((s) => ({
    id: s.id,
    titulo: s.nome,
    subtitulo: TIPO_LABEL[s.tipoRegime] || s.tipoRegime,
    critico: s.tipoRegime === "servico",
    isService: s.tipoRegime === "servico",
    defaults: advancedDefaults(s),
    inputs: { ...advancedDefaults(s), ...(formacaoInputs[s.id] || {}) },
  })), [active, formacaoInputs]);

  const { results, calculating, calculatedAt } = useAdvancedResults(advanced, advancedBase, aliquotas, fp.projectId);

  const segments = useMemo(() => (advanced
    ? advancedBase.map((s, i) => calibrate(s, results[s.id], calculatedAt.current[s.id], active[i], params))
    : simpleSegments(params, formacaoInputs).map((s) => ({ ...s, rule: s.id, matchesServer: true, defaults: SEGMENT_DEFS.find((d) => d.id === s.id).defaults }))),
  [advanced, advancedBase, active, calculatedAt, results, params, formacaoInputs]);

  const fallback = segments.find((s) => s.critico) || segments[0];
  const selected = segments.find((s) => s.id === picked) || fallback;

  const effectiveMode = mode;
  const typed = selected ? prices[selected.id] : undefined;
  const referencePrice = selected?.r?.pvAtual;
  const usingReference = !(Number(typed) > 0);
  const pricedAt = usingReference ? referencePrice : Number(typed);
  // O preço em teste: o digitado, ou, ao buscar o preço, o mínimo no regime futuro
  const testedPrice = effectiveMode === "margin" ? pricedAt : selected?.r?.pvFuturo;

  const hasResult = selected?.r?.pvFuturo > 0;
  const atPrice = useMemo(() => (hasResult
    ? resultAtPrice(selected.rule, selected.inputs, params, effectiveMode === "margin" ? pricedAt : selected.r?.pvFuturo)
    : { r: {}, marginToday: null, marginFuture: null }), [selected, hasResult, effectiveMode, params, pricedAt]);

  const curve = useMemo(() => (hasResult ? buildCurve(selected.rule, selected.inputs, params, testedPrice) : null), [selected, hasResult, params, testedPrice]);
  // Em "Achar o preço", as alavancas respondem: dá para manter o preço de hoje?
  const leverPrice = effectiveMode === "margin" ? pricedAt : referencePrice;
  const levers = useMemo(() => {
    if (!hasResult || !leverPrice) return null;
    const data = buildLevers(selected.rule, selected.inputs, params, leverPrice);
    // Despesa da matriz não se ajusta aqui: é definida no Cadastro
    return selected.opexFromMatrix && data?.levers ? { ...data, levers: data.levers.filter((l) => l.key !== "pctOpex") } : data;
  }, [selected, hasResult, params, leverPrice]);
  const formulas = useMemo(() => (hasResult ? buildFormulas(selected.rule, selected.inputs, params, selected.r) : null), [selected, hasResult, params]);

  if (fp.loading) {
    return (
      <Section>
        <WidgetGrid>
          <Widget><Skeleton $h={420} $r={10} /></Widget>
          <Widget $span={3}><Skeleton $h={420} $r={10} /></Widget>
        </WidgetGrid>
      </Section>
    );
  }

  const isExample = !!selected && Object.keys(selected.defaults).every((k) => Number((selected.rawInputs || selected.inputs)[k] ?? 0) === Number(selected.defaults[k] ?? 0))
    && usingReference;
  const setField = (field, value) => setFormacaoInput(selected.id, field, value);
  const setPrice = (value) => setPrices((prev) => ({ ...prev, [selected.id]: value }));
  const resetSegment = () => {
    Object.entries(selected.defaults).forEach(([k, v]) => setFormacaoInput(selected.id, k, v));
    setPrices((prev) => { const next = { ...prev }; delete next[selected.id]; return next; });
  };
  const pickPrice = (value) => { setPrice(value); setMode("margin"); };
  const applyLever = (lever) => {
    if (lever.key === "price") pickPrice(round2(lever.needed));
    else {
      setField(lever.key, round2(lever.needed));
      setPrice(round2(leverPrice));
      setMode("margin");
    }
  };

  return (
    <>
      {segments.length === 0 && (
        <Section>
          <Notice style={{ marginTop: 0 }}>
            <FiInfo />
            <span>Nenhum segmento ativo. Ative pelo menos um segmento em Cadastro para usar a calculadora.</span>
          </Notice>
        </Section>
      )}

      {selected && (
        <>
          <Section>
            <Picker role="group" aria-label="Segmento">
              {segments.map((s) => {
                const d = deltaOf(s.r);
                return (
                  <PickerCard key={s.id} type="button" $on={s.id === selected.id} aria-pressed={s.id === selected.id} onClick={() => setPicked(s.id)}>
                    <div className="h">{dot(d === null ? IOS.gray : directionColor(d))} {s.titulo}</div>
                    <div className="row">
                      <span className="v" title={money(s.r?.pvFuturo)}><Num value={s.r?.pvFuturo} format={money} /></span>
                      <span className="d" style={{ color: d === null ? undefined : directionColor(d) }}><Num value={d} format={formatDelta} /></span>
                    </div>
                    <div className="f">Preço mínimo com CBS/IBS · hoje <Num value={s.r?.pvAtual} format={money} /></div>
                  </PickerCard>
                );
              })}
            </Picker>
          </Section>

          <Section $delay={60}>
            <WidgetGrid>
              <Calculator
                segment={selected}
                mode={effectiveMode}
                onMode={setMode}
                allowPriceMode
                inputs={selected.inputs}
                defaults={selected.defaults}
                params={params}
                baseValue={testedPrice}
                onChange={setField}
                onReset={resetSegment}
                isExample={isExample}
                price={usingReference ? (referencePrice ? round2(referencePrice) : "") : typed}
                onPrice={setPrice}
                referencePrice={referencePrice}
                usingReference={usingReference}
              />
              <Result
                segment={selected}
                mode={effectiveMode}
                atPrice={atPrice}
                inputs={selected.inputs}
                testedPrice={testedPrice}
                curve={curve}
                calculating={calculating}
                onPickPrice={pickPrice}
              />
            </WidgetGrid>
            <Notice>
              <FiInfo />
              <span>
                {advanced && "O preço vem do servidor, e a curva e as tabelas usam a mesma base. "}
                {selected.opexFromMatrix && `As despesas operacionais deste segmento, ${pct1(selected.inputs.pctOpex)}, vêm da matriz do Cadastro. `}
                {!selected.matchesServer && "Neste segmento, a curva e as tabelas são uma estimativa e podem diferir do preço do servidor. "}
                {`Os valores ficam salvos neste navegador. Em cada percentual, ${PERCENT_BASE}. O traço na régua marca o valor de exemplo.`}
              </span>
            </Notice>
          </Section>

          {hasResult && (
            <>
              <Section $delay={120}>
                <PriceSplit>
                  <PriceBreakdown segmentId={selected.rule} inputs={selected.inputs} params={params} price={testedPrice} delay={200} />
                  {levers && <Levers data={levers} price={leverPrice} onApply={applyLever} />}
                </PriceSplit>
              </Section>
              <Section $delay={160}>
                <WidgetGrid>
                  <FormulaCard formulas={formulas} segment={selected} />
                  <RatesCard params={params} advanced={advanced} eligible={selected.inputs.despesasElegiveis} />
                </WidgetGrid>
              </Section>
            </>
          )}
        </>
      )}

      {advanced && (
        <Section $delay={200}>
          <WidgetGrid>
            {segments.length > 0 && <Scenarios projectId={fp.projectId} segments={segments} aliquotas={aliquotas} />}
          </WidgetGrid>
        </Section>
      )}
    </>
  );
}
